import { useState } from 'react';
import { App, Button, Tooltip } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';

/**
 * 一键复制。
 *
 * navigator.clipboard 只在安全上下文（https 或 localhost）里有，内网 http 部署时是
 * undefined —— 所以留了一条 textarea + execCommand 的退路，不然按钮在生产环境会静默失效。
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 落到下面的兜底
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface Props {
  text: string;
  /** 按钮上的文字；不给就只显示图标。 */
  label?: string;
  tooltip?: string;
  size?: 'small' | 'middle';
  type?: 'text' | 'link' | 'default';
  /** 复制成功后的提示语。 */
  successMessage?: string;
}

export default function CopyButton({
  text,
  label,
  tooltip = '复制',
  size = 'small',
  type = 'text',
  successMessage = '已复制',
}: Props) {
  const { message } = App.useApp();
  const [done, setDone] = useState(false);

  async function run(e: React.MouseEvent) {
    // 表格里整行是可点的（打开详情），复制不该顺带把抽屉也打开
    e.stopPropagation();
    if (await copyText(text)) {
      setDone(true);
      message.success(successMessage);
      setTimeout(() => setDone(false), 1500);
    } else {
      message.error('复制失败，请手动选中复制');
    }
  }

  return (
    <Tooltip title={tooltip}>
      <Button
        type={type}
        size={size}
        // 有文字时就让文字当无障碍名字；再套一个 aria-label 会把它盖掉，
        // 读屏和测试看到的就都是「复制」而不是「复制全部」
        aria-label={label ? undefined : tooltip}
        icon={done ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
        onClick={(e) => void run(e)}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
